import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { X, Image as ImageIcon, Box, Video, Play } from 'lucide-react';

export function DishDialog({
    open, onOpenChange, editing, dishForm, setDishForm, categories,
    onSave, onImageUpload, onModelUpload, onVideoUpload,
    uploadingImage, uploadingModel, uploadingVideo
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{editing ? 'Edit Dish' : 'New Dish'}</DialogTitle>
                    <DialogDescription>
                        {editing ? 'Update the dish details below.' : 'Add a new dish to your menu with an optional 3D model for AR viewing.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Dish Name *</Label>
                            <Input
                                value={dishForm.name}
                                onChange={(e) => setDishForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Grilled Salmon"
                                className="border-[#E5E1D1] rounded-xl"
                                data-testid="dish-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Price *</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={dishForm.price}
                                onChange={(e) => setDishForm(prev => ({ ...prev, price: e.target.value }))}
                                placeholder="0.00"
                                className="border-[#E5E1D1] rounded-xl"
                                data-testid="dish-price-input"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select value={dishForm.category_id} onValueChange={(v) => setDishForm(prev => ({ ...prev, category_id: v }))}>
                            <SelectTrigger className="border-[#E5E1D1] rounded-xl" data-testid="dish-category-select">
                                <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((cat) => (
                                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                            value={dishForm.description}
                            onChange={(e) => setDishForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Describe the dish"
                            className="border-[#E5E1D1] rounded-xl"
                            rows={2}
                            data-testid="dish-description-input"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Cover Image</Label>
                        <div className="flex items-center gap-4">
                            {dishForm.image_url && (
                                <div className="relative">
                                    <img src={dishForm.image_url} alt="Preview" className="w-20 h-20 object-cover rounded-xl" />
                                    <button 
                                        onClick={() => setDishForm(prev => ({ ...prev, image_url: '' }))}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                            <label className="cursor-pointer">
                                <input type="file" accept="image/*" onChange={onImageUpload} className="hidden" />
                                <Button type="button" variant="outline" className="gap-2" disabled={uploadingImage} asChild>
                                    <span>
                                        <ImageIcon className="w-4 h-4" />
                                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                    </span>
                                </Button>
                            </label>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>3D Model (.glb)</Label>
                        {dishForm.model_url ? (
                            <div className="space-y-3">
                                <div className="relative h-48 rounded-xl overflow-hidden bg-[#0D0D0D]">
                                    <model-viewer
                                        src={dishForm.model_url}
                                        alt="3D Preview"
                                        camera-controls
                                        auto-rotate
                                        auto-rotate-delay="0"
                                        rotation-per-second="20deg"
                                        shadow-intensity="1"
                                        exposure="1.2"
                                        environment-image="neutral"
                                        style={{ width: '100%', height: '100%' }}
                                        data-testid="model-preview"
                                    />
                                    <div className="absolute top-2 right-2 px-2 py-1 bg-[#C9A227] text-white text-xs rounded-full flex items-center gap-1">
                                        <Box className="w-3 h-3" /> 3D
                                    </div>
                                    <button 
                                        onClick={() => setDishForm(prev => ({ ...prev, model_url: '' }))}
                                        className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                                <p className="text-xs text-[#69665B] text-center">Drag to rotate - Pinch to zoom</p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-4">
                                <label className="cursor-pointer flex-1">
                                    <input type="file" accept=".glb" onChange={onModelUpload} className="hidden" />
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${uploadingModel ? 'border-[#C9A227] bg-[#C9A227]/5' : 'border-[#E5E1D1] hover:border-[#C9A227]'}`}>
                                        {uploadingModel ? (
                                            <div className="flex flex-col items-center">
                                                <div className="w-8 h-8 border-2 border-[#C9A227] border-t-transparent rounded-full animate-spin mb-2" />
                                                <p className="text-sm text-[#C9A227]">Uploading 3D model...</p>
                                            </div>
                                        ) : (
                                            <>
                                                <Box className="w-8 h-8 mx-auto text-[#969282] mb-2" />
                                                <p className="text-sm text-[#69665B]">Click to upload .glb file</p>
                                                <p className="text-xs text-[#969282] mt-1">For AR viewing on mobile devices</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>
                    {/* Video Upload */}
                    <div className="space-y-2">
                        <Label>Short Video (mp4)</Label>
                        {dishForm.video_url ? (
                            <div className="space-y-3">
                                <div className="relative h-48 rounded-xl overflow-hidden bg-[#0D0D0D]">
                                    <video
                                        src={dishForm.video_url}
                                        className="w-full h-full object-cover"
                                        autoPlay muted loop playsInline
                                        data-testid="video-preview"
                                    />
                                    <div className="absolute top-2 right-2 px-2 py-1 bg-purple-600 text-white text-xs rounded-full flex items-center gap-1">
                                        <Play className="w-3 h-3" /> Video
                                    </div>
                                    <button
                                        onClick={() => setDishForm(prev => ({ ...prev, video_url: '' }))}
                                        className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-4">
                                <label className="cursor-pointer flex-1">
                                    <input type="file" accept="video/mp4,video/webm" onChange={onVideoUpload} className="hidden" />
                                    <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all ${uploadingVideo ? 'border-purple-500 bg-purple-500/5' : 'border-[#E5E1D1] hover:border-purple-500'}`}>
                                        {uploadingVideo ? (
                                            <div className="flex flex-col items-center">
                                                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-2" />
                                                <p className="text-sm text-purple-500">Uploading video...</p>
                                            </div>
                                        ) : (
                                            <>
                                                <Video className="w-8 h-8 mx-auto text-[#969282] mb-2" />
                                                <p className="text-sm text-[#69665B]">Upload short video (3-6s)</p>
                                                <p className="text-xs text-[#969282] mt-1">MP4 or WebM format</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>
                    {/* Display Options */}
                    <div className="space-y-3 border-t border-[#E5E1D1] pt-4">
                        <Label className="text-sm font-semibold">Display Options</Label>
                        <div className="grid grid-cols-3 gap-3">
                            <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${dishForm.enable_image_view ? 'border-[#C75D4E] bg-[#C75D4E]/5' : 'border-[#E5E1D1]'}`}>
                                <input type="checkbox" checked={dishForm.enable_image_view} onChange={e => setDishForm(prev => ({ ...prev, enable_image_view: e.target.checked }))} className="accent-[#C75D4E]" data-testid="toggle-image" />
                                <span className="text-xs font-medium text-[#2B2A27]">Image</span>
                            </label>
                            <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${dishForm.enable_video_view ? 'border-purple-500 bg-purple-500/5' : 'border-[#E5E1D1]'}`}>
                                <input type="checkbox" checked={dishForm.enable_video_view} onChange={e => setDishForm(prev => ({ ...prev, enable_video_view: e.target.checked }))} className="accent-purple-500" data-testid="toggle-video" />
                                <span className="text-xs font-medium text-[#2B2A27]">Video</span>
                            </label>
                            <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${dishForm.enable_ar_view ? 'border-[#4F7C61] bg-[#4F7C61]/5' : 'border-[#E5E1D1]'}`}>
                                <input type="checkbox" checked={dishForm.enable_ar_view} onChange={e => setDishForm(prev => ({ ...prev, enable_ar_view: e.target.checked }))} className="accent-[#4F7C61]" data-testid="toggle-ar" />
                                <span className="text-xs font-medium text-[#2B2A27]">AR (3D)</span>
                            </label>
                        </div>
                        {/* Warnings */}
                        {dishForm.enable_image_view && !dishForm.image_url && (
                            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg" data-testid="warn-image">Image enabled but no image uploaded</p>
                        )}
                        {dishForm.enable_video_view && !dishForm.video_url && (
                            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg" data-testid="warn-video">Video enabled but no video uploaded</p>
                        )}
                        {dishForm.enable_ar_view && !dishForm.model_url && (
                            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg" data-testid="warn-ar">AR enabled but no 3D model uploaded</p>
                        )}
                    </div>
                    {/* Display Mode */}
                    <div className="space-y-2">
                        <Label>Display Mode</Label>
                        <Select value={dishForm.display_type} onValueChange={(v) => setDishForm(prev => ({ ...prev, display_type: v }))}>
                            <SelectTrigger className="border-[#E5E1D1] rounded-xl" data-testid="display-type-select">
                                <SelectValue placeholder="Select display mode" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="auto">Auto (recommended)</SelectItem>
                                <SelectItem value="image">Image</SelectItem>
                                <SelectItem value="video">Video</SelectItem>
                                <SelectItem value="ar">AR (3D Model)</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-[#969282]">
                            {dishForm.display_type === 'auto' 
                                ? 'Auto: Shows Video → AR → Image (based on available media)'
                                : `Override: Always show ${dishForm.display_type} mode`}
                        </p>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button onClick={onSave} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white" data-testid="save-dish-btn">
                            {editing ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
